// Adapter: Designer V2 Camera entity → bandwidthMath CameraBandwidthInput.
//
// Kept thin on purpose. The bandwidthMath module is the source of truth for
// math; this file only translates field names and parses the resolution
// string into megapixels (Camera stores e.g. "2688x1520", math needs MP).

import type { Camera } from '@/components/cctv-designer-v2/types';
import type { CameraBandwidthInput } from './bandwidthMath';

// Catalog buckets matching BITRATE_MAP_H264 keys. We round to the closest
// bucket so a 3 MP camera (rare) lands on 2 MP, 6 MP on 5 MP, 10 MP on 8 or 12.
const MP_BUCKETS = [1, 2, 4, 5, 8, 12, 16] as const;

export function resolutionToMegapixels(resolution: string | undefined): number {
  if (!resolution) return 4; // sensible fallback for projects without catalog match
  const match = resolution.match(/(\d+)\s*[xX×]\s*(\d+)/);
  if (!match) return 4;
  const w = parseInt(match[1], 10);
  const h = parseInt(match[2], 10);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 4;
  const mp = (w * h) / 1_000_000;
  return nearestBucket(mp);
}

function nearestBucket(mp: number): number {
  let best: number = MP_BUCKETS[0];
  let bestDist = Math.abs(MP_BUCKETS[0] - mp);
  for (const b of MP_BUCKETS) {
    const d = Math.abs(b - mp);
    if (d < bestDist) {
      bestDist = d;
      best = b;
    }
  }
  return best;
}

export function cameraToBandwidthInput(camera: Camera): CameraBandwidthInput {
  const o = camera.bandwidthOverride;
  return {
    id: camera.id,
    name: camera.name,
    type: camera.type,
    megapixels: resolutionToMegapixels(camera.resolution),
    fpsMode: o?.fpsMode,
    fps: o?.fps,
    codecOverride: o?.codec,
    sceneOverride: o?.scene,
    bitrateOverride: o?.bitrateMbps,
  };
}
